import { IPurchaseOrdersGraphQLFilter } from "@shared/interfaces";

import {
  customUnitOfMeasurementFields,
  idAndNameFields,
  unitOfMeasurementFields,
} from "./queries.constants";
import { QueriesUtils } from "./queries.utils";

export const getPurchaseOrdersGraphQLQuery = (
  orgId: string,
  filter: IPurchaseOrdersGraphQLFilter,
  first: number,
  last: number,
  after: string,
  before: string,
  include: string[],
): string => {
  const filterClause = QueriesUtils.generateFilterClause(filter);
  const afterCursorClause = first > 0 ? QueriesUtils.generateClause("after", after) : "";
  const firstClause = first > 0 ? QueriesUtils.generateClause("first", first) : "";
  const beforeCursorClause = last > 0 ? QueriesUtils.generateClause("before", before) : "";
  const lastClause = last > 0 ? QueriesUtils.generateClause("last", last) : "";

  return `
{
    loadPurchaseOrders(   
    orgId: "${orgId}",
    ${filterClause ? `${filterClause},` : ""}
    ${afterCursorClause ? `${afterCursorClause},` : ""}
    ${beforeCursorClause ? `${beforeCursorClause},` : ""}
    ${firstClause ? `${firstClause},` : ""}
    ${lastClause ? `${lastClause},` : ""}
) {
        ${getPurchaseOrdersProperties(include)}  
    }
}
`;
};

/**
 * Generates a GraphQL query to load purchase orders based on specified parameters.
 *
 * @param {string} orgId - (Required) The organization ID for which purchase orders are queried.
 * @param {string[]} ids - (Required) A list of purchase orders ids
 * @param {number} first - The number of purchase orders to retrieve in a single page (for forward pagination).
 * @param {number} last - The number of purchase orders to retrieve in a single page (for backward pagination).
 * @param {string} after - The cursor indicating the end of the previous page for forward pagination.
 * @param {string} before - The cursor indicating the start of the next page for backward pagination.
 * @param {string[]} include - Additional fields to include in the query.
 * @returns {string} The generated GraphQL query for loading purchase orders.
 */

export const getPurchaseOrdersByIdsGraphQLQuery = (
  orgId: string,
  ids: string[],
  first: number,
  last: number,
  after: string,
  before: string,
  include: string[],
): string => {
  const afterCursorClause = first > 0 ? QueriesUtils.generateClause("after", after) : "";
  const firstClause = first > 0 ? QueriesUtils.generateClause("first", first) : "";
  const beforeCursorClause = last > 0 ? QueriesUtils.generateClause("before", before) : "";
  const lastClause = last > 0 ? QueriesUtils.generateClause("last", last) : "";

  return `
  {
      loadPurchaseOrdersByIds(   
      orgId: "${orgId}",
      ids: ${QueriesUtils.getParsedIds(ids)},
      ${afterCursorClause ? `${afterCursorClause},` : ""}
      ${beforeCursorClause ? `${beforeCursorClause},` : ""}
      ${firstClause ? `${firstClause},` : ""}
      ${lastClause ? `${lastClause},` : ""}
  ) {
          ${getPurchaseOrdersProperties(include)}      
      }
  }
  `;
};

export const getPurchaseOrdersProperties = (include: string[]): string => `
        pageInfo {
            hasNextPage 
            hasPreviousPage
            startCursor
            endCursor
        }
        totalCount
        edges {
            cursor
            node {
                id
                recordState
                number
                status
                orderDate
                expectedDeliveryDate
                supplier {
                    ${idAndNameFields}                    
                }
                buyer {
                    ${idAndNameFields}                    
                }
                deliverToLocation {
                    ${idAndNameFields}
                }
                note
                lineItems {
                    lineItemId
                    product {
                        ${idAndNameFields}
                        unitOfMeasurement {
                            ${unitOfMeasurementFields}                
                        }
                        defaultCustomUnit { 
                            ${customUnitOfMeasurementFields}
                        }
                    }
                    quantity
                    note
                }
                createdTime
                createdBy
                updatedBy
                updatedTime
                comments {
                    text
                    createdTime
                    createdBy
                    updatedTime
                    updatedBy
                    emailAddress
                }
                tags {
                    color
                    title
                    createdTime
                    createdBy
                    updatedTime
                    updatedBy
                }
                ${QueriesUtils.getQueryIncludes(include)}
            }            
        } 
`;
